import {
  downloadToCSV,
  TimeLineTable,
  useTimeZone,
  useTranslation,
  zonedToUTC,
  zonedToUTCAdd1Day
} from '@fareye/common'
import { LANGUAGE_STRINGS, TRN_CONSTANTS } from '@fareye/configs'
import { AllIssueListing } from '@fareye/issue-resolution'
import { Flex, RenderControl } from '@fareye/ui'
import {
  camelCase,
  cancelToken,
  currenthhmmA,
  dMmm,
  getQueryParams,
  getXlsx,
  groupBy,
  HEADERS,
  isEmpty,
  post,
  postXlsx,
  serialzeAggregationsToSummaryData,
  snakeCase
} from '@fareye/utils'
import queryString from 'query-string'
import React, { useMemo, useRef } from 'react'
import { mapping } from '../../../config/mapComponents'
import { getTableColumns } from '../components/Column'

const initApiResponse = {
  data: [],
  isLoading: false,
  isError: false,
  isSuccess: true,
  message: '',
  meta: {
    count: 0,
    pageNumber: 1
  }
}
const aggs = ['status', 'milestone_carrier_status']
const TripTableMultiModel = React.memo((props: any) => {
  let {
    // data,
    match,
    config,
    baseUrl,
    endDate,
    type = 'forward',
    fileName,
    location,
    startDate,
    setLastUpdate,
    selectedKey,
    searchQuery,
    showDatePicker = true,
    refreshableTable = true,
    globalSearch,
    setSummaryData,
    summaryData,
    selectedAggsFilter,
    showTimezone,
    setNotFoundList = [],
    selectedFilters,
    setSelectedFilters,
    setFoundCount
  } = props

  // TODO : Columns to be passed only from unique prop
  //const [isPasteSearch, setIspasteSearch] = React.useState<boolean>(false)
  let isPasteSearch: boolean = false
  const [checkMultipleSearch, setCheckMultipleSearch] = React.useState<boolean>(
    false
  )
  const [recordUsingTab, setRecordUsingTab] = React.useState<boolean>(false)

  const [downloadLoading, setDownloadLoading] = React.useState<boolean>(true)
  const [apiData, setApiResponse] = React.useState<any>(initApiResponse)
  const [sorting, setSortBy] = React.useState({
    sortBy: '',
    sortByDirection: true
  })
  const source = cancelToken().source()
  const [changeDetect, setChangeDetect] = React.useState(false)
  const [pageNo, setPageNo] = React.useState(0)
  const [recordsPerPage, setRecordsPerPage] = React.useState(50)
  const query = queryString.parse(location.search)
  const [showCommentModule, setShowCommentModule] = React.useState(false)
  const [refreshTable, setRefreshTable] = React.useState(false)
  const isFirstTime = useRef(false)
  const { style, className, variant } = config.styling
  const { state } = useTimeZone()
  const {
    links,
    apiConfig,
    downloadApiConfig,
    sortingMapping,
    showTimeLine = true,
    issueRaisedConfig
  } = config.config
  const { sortBy, sortByDirection } = sorting
  const searchParams = {
    ...query,
    // startDate,
    // endDate,
    pageNo: pageNo + 1,
    recordsPerPage,
    sortByDirection: sortBy ? (sortByDirection ? 'desc' : 'asc') : '',
    ...searchQuery
    // ...(apiConfig && apiConfig.queryParams)
  }
  const queryParam = getQueryParams(searchParams)

  const subComponentConfig = config.children
  const tableLinks: any = { ...links }
  const { t } = useTranslation()

  const selectedColumn = React.useMemo(() => {
    const columns =
      selectedKey === 'tabular'
        ? config.config.columnsList
        : config.config.columns
    return getTableColumns(columns, {
      links: config.config.links,
      queryParams: {},
      t
    })
  }, [selectedKey])
  const downloadLabel = t('Download')
  const issueLabel = t('Issues Raised')
  const refreshLabel = t('Refresh')

  React.useEffect(() => {
    fetchData()
  }, [pageNo, changeDetect, sorting, recordUsingTab])
  React.useEffect(() => {
    if (isFirstTime.current && refreshTable) fetchData()
  }, [refreshTable])

  React.useEffect(() => {
    if (isFirstTime.current) {
      setPageNo(0)
      setRecordsPerPage(10)
      setApiResponse(initApiResponse)
      setChangeDetect(!changeDetect)
    }
    isFirstTime.current = true
  }, [startDate, endDate, globalSearch, type, selectedAggsFilter])

  function generateFilterDataFromGlobalSearch(globalSearch: any[]) {
    if (isEmpty(globalSearch)) {
      return []
    } else {
      const groupedSearch = groupBy(globalSearch, ele => ele.key)
      return Object.entries(groupedSearch).map(([key, value]) => {
        return [
          {
            filterKey: key,
            filterValue: value.reduce((res = [], obj) => {
              if (typeof obj.value === 'string') {
                res.push(obj.value)
              } else {
                res.push(...obj.value)
                isPasteSearch = true
              }
              return res
            }, []),
            condition: 'include',
            filterType: 'eq'
          }
        ]
      })
    }
  }

  function generateFilterObjectForDateRange() {
    const startFilter = [
      {
        filterKey: 'start_date',
        filterValue: startDate ? zonedToUTC(startDate) : new Date(),
        condition: 'include',
        filterType: 'gte'
      }
    ]
    const endFilter = [
      {
        filterKey: 'start_date',
        filterValue: endDate ? zonedToUTCAdd1Day(endDate) : new Date(),
        condition: 'include',
        filterType: 'lt'
      }
    ]
    return showDatePicker ? [startFilter, endFilter] : []
  }

  function getParcelTypeObj() {
    const parcelType = [
      {
        filterKey: type.mapping,
        filterValue: type.value,
        condition: 'include',
        filterType: 'eq'
      }
    ]
    return parcelType
  }

  function generateFilterDataFromAggregation() {
    let aggsFilter: any[] = []
    Object.entries(selectedAggsFilter).forEach(([key, value]) => {
      if (selectedAggsFilter[key].length > 0) {
        aggsFilter.push([
          {
            filterKey: snakeCase(key),
            filterValue: selectedAggsFilter[key],
            condition: 'include',
            filterType: 'eq'
          }
        ])
      }
    })
    const { queryParams } = apiConfig
    let statusFilter: any[] = []
    Object.keys(queryParams).forEach((item, index) => {
      if (
        !selectedAggsFilter?.hasOwnProperty(
          camelCase(queryParams[item]['filterKey'])
        )
      ) {
        statusFilter.push([queryParams[item]])
      }
    })
    return [...aggsFilter, ...statusFilter]
  }

  function generateSortingObj() {
    const { sortByDirection, sortBy } = sorting
    if (sortBy) {
      const sort = [{ sortBy, sortDir: sortByDirection ? 'asc' : 'desc' }]
      return { sort }
    } else {
      return {}
    }
  }

  async function fetchData() {
    try {
      setApiResponse({
        ...apiData,
        isLoading: true
      })
      let globalFilter = generateFilterDataFromGlobalSearch(globalSearch)
      let totalSearchData: any[] = []
      setCheckMultipleSearch(isPasteSearch)
      if (isPasteSearch && !isEmpty(globalSearch)) {
        const searchData: any[] = globalFilter[0].filter(
          (i: any) => i.filterKey === 'awb_tracking_no'
        )[0]?.filterValue
        totalSearchData = searchData.filter(
          (value, index) => searchData.indexOf(value) === index
        )
      }
      const dateFilter = generateFilterObjectForDateRange()
      const aggsFilter = generateFilterDataFromAggregation()
      let filter = [getParcelTypeObj(), ...aggsFilter]
      filter = filter.concat(globalFilter).concat(dateFilter)
      filter = filter.filter(ele => {
        return !isEmpty(ele)
      })
      const { apiURL } = apiConfig
      const {
        data: { results, meta, aggsMeta }
      } = await post(
        apiURL,
        {
          filter,
          perPage: isPasteSearch ? totalSearchData.length : recordsPerPage,
          page: pageNo + 1,
          aggs,
          ...generateSortingObj()
        },
        source.token,
        {
          headers: HEADERS()['HEADER_INFLECTION']
        }
      )
      const tempData = [...apiData.data]
      tempData.splice(pageNo * recordsPerPage, recordsPerPage, ...results)
      setApiResponse({
        ...apiData,
        data: tempData,
        isSuccess: true,
        meta: meta,
        isError: false,
        message: isEmpty(tempData) ? 'No Data Found...' : '',
        isLoading: false
      })
      setRefreshTable(false)
      setLastUpdate(currenthhmmA())
      setSummaryData(serialzeAggregationsToSummaryData(aggsMeta))

      if (isPasteSearch && !isEmpty(globalSearch)) {
        const totalFoundData: any[] = tempData.map((i: any) => i.trackingId)
        calcNotFoundData(totalSearchData, totalFoundData)
      }
    } catch (ex:any) {
      setApiResponse({
        data: [],
        isSuccess: false,
        isError: true,
        message: 'Something went wrong...',
        isLoading: false
      })
    }
  }

  function calcNotFoundData(totalSearchData: any[], totalFoundData: any[]) {
    setFoundCount(totalFoundData.length)
    let missing = totalSearchData.filter(
      item => totalFoundData.indexOf(item) < 0
    )
    setNotFoundList(missing)
  }
  const isDisabledForDateRange = React.useMemo(() => {
    var startTime = (startDate ? zonedToUTC(startDate) : new Date()).getTime()
    var endTime = (endDate ? zonedToUTCAdd1Day(endDate) : new Date()).getTime()
    return Boolean(
      startTime &&
        endTime &&
        (endTime - startTime) / 1000 / 60 / 60 / 24 >
          TRN_CONSTANTS.maxDownloadDays
    )
  }, [startDate, endDate])

  const downloadTitle = isDisabledForDateRange
    ? t(LANGUAGE_STRINGS.MAX_DOWNLOAD, {
        MAX_DOWNLOAD: TRN_CONSTANTS.maxDownloadDays
      })
    : t('Export as Excel')

  console.log('0000000000', isDisabledForDateRange, startDate, endDate)
  async function downloadCSV() {
    let selectedType = type.value
    try {
      let {
        type = 'GET',
        apiURL = '',
        queryParams = {},
        subType
      } = downloadApiConfig
      apiURL = `${apiURL}/${selectedType}`
      setDownloadLoading(true)
      delete searchParams.pageNo
      delete searchParams.recordsPerPage
      const queryParam = getQueryParams({
        ...searchParams,
        ...queryParams,
        plant: props.plant ? props.plant : ''
      })
      let filter: any = generateFilterDataFromGlobalSearch(globalSearch)
      const dateFilter = generateFilterObjectForDateRange()
      const aggsFilter = generateFilterDataFromAggregation()
      filter = [...filter, ...dateFilter, ...aggsFilter]
      const { data }: any = await (type === 'GET'
        ? getXlsx(`${apiURL}?${queryParam}`, source.token)
        : postXlsx(apiURL, { filter, subType }, source.token))
      downloadToCSV({
        data,
        fileName: fileName || `${dMmm()}`,
        type: 'text/zip',
        extension: 'zip'
      })
      setDownloadLoading(false)
    } catch (ex:any) {
      setDownloadLoading(false)
    }
  }
  const totalCount = useMemo(() => {
    let count = 0
    if (summaryData != null && typeof summaryData == 'object') {
      const attrKey = Object.keys(summaryData)
      if (attrKey?.length > 0) {
        count = summaryData[attrKey[1]]?.count
      }
    }
    return count || apiData?.meta?.count
  }, [summaryData])
  return (
    <RenderControl state={apiData} variant={'xl'} t={t}>
      <Flex column>
        <div
          style={{
            ...{ padding: '15px 15px 0 15px', marginBottom: '15px' },
            ...style
          }}
        >
          <TimeLineTable
            variant={selectedKey === 'tabular' ? '' : 'card'}
            className={`responsive  ${className} multi-model`}
            data={apiData.data}
            size="s"
            style={{
              table: {
                maxHeight: 'calc(100vh - 360px)'
              }
            }}
            totalElements={totalCount}
            showTotalCount={false}
            totalCountText={`${t('Total Shipments')}: `}
            columns={selectedColumn}
            downloadCSV={downloadCSV}
            downloadTitle={downloadTitle}
            downloadLabel={downloadLabel}
            downloadDisabled={isDisabledForDateRange}
            commentingModule={() => setShowCommentModule(true)}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            issueLabel={issueLabel}
            defaultPageSize={
              checkMultipleSearch ? apiData?.meta?.count : recordsPerPage
            }
            onPageSizeChange={(pageSize: number) => {
              setPageNo(0)
              setRecordsPerPage(pageSize)
              setRecordUsingTab(!recordUsingTab)
            }}
            refresh={setRefreshTable}
            refreshLabel={refreshLabel}
            subComponent={
              selectedKey !== 'tabular' && showTimeLine
                ? ({ row: { original } }: any) => {
                    return (
                      selectedKey !== 'tabular' &&
                      subComponentConfig &&
                      Object.keys(subComponentConfig).map(
                        (subComp: any, i: number) => {
                          const Comp: any = (mapping as any)[
                            subComponentConfig[subComp].component
                          ]
                          return (
                            <Comp
                              key={`sub-component-${i}`}
                              baseUrl={baseUrl}
                              rowData={original}
                              timelineData={original.milestoneData}
                            />
                          )
                        }
                      )
                    )
                  }
                : undefined
            }
            onSortedChange={(ev: any) => {
              setSortBy({
                sortBy: sortingMapping[ev[0].id]
                  ? sortingMapping[ev[0].id]
                  : ev[0].id,
                sortByDirection:
                  ev[0].id === sorting.sortBy
                    ? !sorting.sortByDirection
                    : ev[0].desc
              })
            }}
            defaultPageIndex={pageNo}
            onPageChange={(pageIndex: number) => setPageNo(pageIndex)}
          />
        </div>
      </Flex>
      {showCommentModule && (
        <AllIssueListing
          showDrawer={showCommentModule}
          setShowDrawer={setShowCommentModule}
          resourceId={''}
          config={issueRaisedConfig}
          resourceType={'Shipment'}
          baseUrl={''}
          updateIssueCount={() => {}}
          startDate={new Date(startDate)}
          endDate={new Date(endDate)}
          showTimezone={showTimezone}
          t={t}
        />
      )}
    </RenderControl>
  )
})

// export default withRouter(TripTable)
export default TripTableMultiModel
